<app-page-breadcrumb [pageTitle]="isEditing ? 'Editar Plantilla' : 'Nueva Plantilla de Cotización'"></app-page-breadcrumb>

<div class="space-y-3 mb-4">
  <!-- Loading -->
  @if (loading) {
  <div class="flex justify-center items-center py-12">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
    <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando plantilla...</span>
  </div>
  }

  <!-- Error -->
  @if (error) {
  <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-error-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  </div>
  }

  <!-- Success -->
  @if (successMessage) {
  <div class="p-4 bg-success-50 border border-success-200 rounded-lg dark:bg-success-500/10 dark:border-success-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-success-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <p class="text-success-600 dark:text-success-400">{{ successMessage }}</p>
    </div>
  </div>
  }
</div>

@if (!loading) {
<form [formGroup]="form" (ngSubmit)="guardar()">
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

    <!-- LEFT COLUMN -->
    <div class="lg:col-span-2 space-y-4">

      <!-- Card: Basic Info -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Información General</h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Name -->
          <div class="sm:col-span-2">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Nombre <span class="text-error-500">*</span>
            </label>
            <input type="text" formControlName="name" placeholder="Ej. Plantilla Corporativa"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              [class.border-error-500]="hasError('name')">
            @if (hasError('name')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('name') }}</p>
            }
          </div>

          <!-- Logo -->
          <div class="sm:col-span-2">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Logo</label>

            <div class="flex items-start gap-4">
              <!-- Preview -->
              <div class="shrink-0 w-24 h-24 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                @if (logoPreviewUrl) {
                <img [src]="logoPreviewUrl" alt="Logo" class="max-w-full max-h-full object-contain">
                } @else {
                <svg class="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                }
              </div>

              <div class="flex-1 space-y-2">
                <div class="flex items-center gap-2">
                  <label
                    class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer inline-flex items-center gap-1.5"
                    [class.opacity-50]="logoUploading" [class.pointer-events-none]="logoUploading">
                    @if (logoUploading) {
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    Subiendo...
                    } @else {
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                    </svg>
                    Subir logo
                    }
                    <input type="file" accept="image/*" class="hidden" (change)="onLogoSelected($event)" [disabled]="logoUploading">
                  </label>

                  @if (logoPreviewUrl) {
                  <button type="button" (click)="removeLogo()"
                    class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20">
                    Quitar
                  </button>
                  }
                </div>

                <!-- Logo URL (manual fallback / external URL) -->
                <input type="text" formControlName="logoUrl" placeholder="o pega una URL: https://..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  [class.border-error-500]="hasError('logoUrl')">
                @if (hasError('logoUrl')) {
                <p class="mt-1 text-xs text-error-600">{{ getError('logoUrl') }}</p>
                }
              </div>
            </div>
          </div>

          <!-- Primary Color -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Color Primario</label>
            <div class="flex items-center gap-2">
              <input type="color" formControlName="primaryColor"
                class="h-9 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-0.5">
              <input type="text" formControlName="primaryColor" placeholder="#3B82F6"
                class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
            </div>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Color de marca: título "COTIZACIÓN", líneas divisorias, encabezado de la tabla de ítems y el TOTAL.
            </p>
          </div>

          <!-- Secondary Color -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Color Secundario</label>
            <div class="flex items-center gap-2">
              <input type="color" formControlName="secondaryColor"
                class="h-9 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-0.5">
              <input type="text" formControlName="secondaryColor" placeholder="#64748B"
                class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
            </div>
            <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Acento de apoyo: etiquetas Proveedor/Cliente, filas alternas de la tabla, fondo del bloque TOTAL
              y encabezados de las cajas de condiciones, términos y cláusulas. Vacío = se usa el color primario.
            </p>
          </div>
        </div>
      </div>

      <!-- Card: Header / Footer -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Encabezado y Pie</h2>

        <div class="space-y-4">
          <!-- Header Text -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Texto de Encabezado</label>
            <textarea formControlName="headerText" rows="2" placeholder="Ej. Nombre de la empresa, RUC, dirección..."
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              [class.border-error-500]="hasError('headerText')"></textarea>
            @if (hasError('headerText')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('headerText') }}</p>
            }
          </div>

          <!-- Footer Text -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Texto de Pie de Página</label>
            <textarea formControlName="footerText" rows="2" placeholder="Ej. Gracias por su preferencia..."
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              [class.border-error-500]="hasError('footerText')"></textarea>
            @if (hasError('footerText')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('footerText') }}</p>
            }
          </div>

          <!-- Contact Info Text -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Información de Contacto</label>
            <textarea formControlName="contactInfoText" rows="2" placeholder="Ej. Tel: +51 999 999 999 | ventas@empresa.com"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              [class.border-error-500]="hasError('contactInfoText')"></textarea>
            @if (hasError('contactInfoText')) {
            <p class="mt-1 text-xs text-error-600">{{ getError('contactInfoText') }}</p>
            }
          </div>
        </div>
      </div>

      <!-- Card: Default Terms -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Términos y Condiciones Predeterminados</h2>

        <div>
          <textarea formControlName="defaultTermsConditions" rows="6"
            placeholder="Ingrese los términos y condiciones que se incluirán por defecto en las cotizaciones que usen esta plantilla..."
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
            [class.border-error-500]="hasError('defaultTermsConditions')"></textarea>
          @if (hasError('defaultTermsConditions')) {
          <p class="mt-1 text-xs text-error-600">{{ getError('defaultTermsConditions') }}</p>
          }
        </div>
      </div>

    </div>

    <!-- RIGHT COLUMN -->
    <div class="space-y-4">

      <!-- Card: Options -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Opciones</h2>

        <div class="space-y-3">
          <!-- Show Logo -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" formControlName="showLogo"
              class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
            <span class="text-sm text-gray-700 dark:text-gray-300">Mostrar logo</span>
          </label>

          <!-- Show Watermark -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" formControlName="showWatermark"
              class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
            <span class="text-sm text-gray-700 dark:text-gray-300">Mostrar marca de agua</span>
          </label>

          <!-- Watermark Text (shown only when showWatermark is true) -->
          @if (form.get('showWatermark')?.value) {
          <div class="ml-7">
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Texto de Marca de Agua</label>
            <input type="text" formControlName="watermarkText" placeholder="Ej. BORRADOR"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
          </div>
          }

          <!-- Is Default -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" formControlName="isDefault"
              class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
            <span class="text-sm text-gray-700 dark:text-gray-300">Plantilla predeterminada</span>
          </label>

          <!-- Is Active -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" formControlName="isActive"
              class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
            <span class="text-sm text-gray-700 dark:text-gray-300">Plantilla activa</span>
          </label>
        </div>
      </div>

      <!-- Card: Actions -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Acciones</h2>

        <button type="submit" [disabled]="saving"
          class="w-full px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
          @if (saving) {
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          Guardando...
          } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          {{ isEditing ? 'Actualizar Plantilla' : 'Crear Plantilla' }}
          }
        </button>

        @if (isEditing) {
        <button type="button" (click)="previewPdf()" [disabled]="previewing"
          class="w-full px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
          @if (previewing) {
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
          Generando...
          } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
          </svg>
          Vista previa PDF
          }
        </button>
        }

        <button type="button" (click)="cancelar()" [disabled]="saving"
          class="w-full px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          Cancelar
        </button>
      </div>

    </div>
  </div>
</form>
}
